// Pure fMP4 surgery for the client-side download button (no DOM, no fetch —
// the download driver in hls-download.js owns I/O, node tests import this).
//
// Everything here runs on plain Uint8Array (Buffer is a subclass, so node
// buffers work too) — in the BROWSER the fetch body arrives as Uint8Array,
// which has none of Buffer's readUInt32BE/concat/copy methods. All box
// math goes through the tiny u32/u64/w32/concatU8 helpers below.
//
// Our extracted library packages (see track-extractor.js / remuxToHls in
// torrent-streamer.js) are ffmpeg HLS fMP4:
//   master.m3u8  → one video variant (video.m3u8) + N audio renditions
//                  (audio-N.m3u8), each its own init (init-v.mp4 / init-aK.mp4)
//   segment      → styp + sidx + moof + mdat (verified live 2026-09-24)
//
// A browser cannot save an HLS stream, but it CAN download these fragments and
// stitch a real .mp4 — no ffmpeg, no server byte-proxying:
//
//   merged = ftyp(video init) + ONE merged moov + [moof|mdat] pairs
//            interleaved verbatim (video/audio)
//
// The merged moov is REAL muxing, measured against ffprobe (two concatenated
// moov boxes are spec-invalid: ffprobe saw only the first movie and misread
// the fragment stream):
//   • video moov's mvhd — next_track_id patched past both tracks
//   • video moov's trak — untouched (track_id 1, h264 stsd)
//   • audio moov's trak — track_id retagged to 2 (tkhd); its edts rides along
//   • ONE mvex — the video trex (id 1) plus the audio trex retagged to 2;
//     the per-init mehd (single-source fragment duration) is dropped
//   • video moov's remaining boxes (udta…) preserved
//
// Fragment surgery: NONE. Each fragment's tfhd carries default-base-is-moof
// (flag 0x020000, verified on live packages), so trun.data_offset is relative
// to its own moof start — and its mdat follows the moof immediately, exactly
// as in the original segment. Concatenating [moof|mdat] pairs preserves that
// relationship byte-for-byte. styp (segment type) and sidx (segment index)
// are segment-level boxes and are stripped; playback walks from moof.
//
// Interleaving order across tracks does not matter for validity: every movie
// fragment is self-describing (tfdt carries each track's media time), so the
// download interleaves [video i][audio i] and flushes whichever track has
// segments left when the shorter list runs out.

// --- byte helpers (Uint8Array-safe) -----------------------------------------

const u32 = (b, o) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
const u64 = (b, o) => (BigInt(u32(b, o)) << 32n) | BigInt(u32(b, o + 4));
const w32 = (b, o, v) => {
  b[o] = (v >>> 24) & 255;
  b[o + 1] = (v >>> 16) & 255;
  b[o + 2] = (v >>> 8) & 255;
  b[o + 3] = v & 255;
};

/** Concatenate Uint8Array/Buffer parts into one fresh Uint8Array. */
export function concatU8(parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

/** Copy `src` into a mutable fresh Uint8Array (inputs may be read-only views). */
function mutableCopy(src) {
  const out = new Uint8Array(src.length);
  out.set(src);
  return out;
}

// --- box walking ------------------------------------------------------------

/**
 * Split a buffer into top-level ISO-BMFF boxes: [{ type, size, off }].
 * Handles 64-bit largesize (size==1) and to-end (size==0) encodings.
 */
export function boxes(buf, off = 0, end = buf.length) {
  const out = [];
  let p = off;
  while (p + 8 <= end) {
    let size = u32(buf, p);
    const type = String.fromCharCode(buf[p + 4], buf[p + 5], buf[p + 6], buf[p + 7]);
    if (size === 1) {
      if (p + 16 > end) break;
      const big = u64(buf, p + 8);
      if (big > BigInt(Number.MAX_SAFE_INTEGER)) break;
      size = Number(big);
    } else if (size === 0) {
      size = end - p; // box runs to the end of its parent
    }
    if (size < 8 || p + size > end) break;
    out.push({ type, size, off: p });
    p += size;
  }
  return out;
}

/** First top-level box of the given type, or null. */
function firstBox(buf, type) {
  return boxes(buf).find((b) => b.type === type) || null;
}

// Containers we descend into when hunting for a nested box.
const CONTAINERS = new Set(["moov", "trak", "mvex", "edts", "mdia", "minf", "stbl"]);

/** Find a nested box by type inside [off, off+size) — depth-first through containers. */
function findBox(buf, off, size, type) {
  for (const b of boxes(buf, off, off + size)) {
    if (b.type === type) return b;
    if (CONTAINERS.has(b.type)) {
      const hit = findBox(buf, b.off + 8, b.size - 8, type);
      if (hit) return hit;
    }
  }
  return null;
}

/** Assemble a container box (e.g. "moov") from already-sized child buffers. */
function buildBox(type, children) {
  const body = concatU8(children);
  const out = new Uint8Array(8 + body.length);
  w32(out, 0, out.length);
  for (let i = 0; i < 4; i += 1) out[4 + i] = type.charCodeAt(i);
  out.set(body, 8);
  return out;
}

// --- init surgery -----------------------------------------------------------

/**
 * Retag a trak's track id (tkhd.track_id). `trakBuf` must start AT the trak
 * box. Returns a new buffer; the input is untouched.
 */
export function retagTrakTrackId(trakBuf, newId) {
  const work = mutableCopy(trakBuf);
  const trak = boxes(work)[0];
  if (!trak || trak.type !== "trak") throw new Error("retagTrakTrackId: not a trak box");
  const tkhd = findBox(work, trak.off + 8, trak.size - 8, "tkhd");
  if (!tkhd) throw new Error("retagTrakTrackId: trak has no tkhd");
  const version = work[tkhd.off + 8];
  // tkhd: size(4) type(4) version(1) flags(3), then v0: ctime4 mtime4
  // track_id4 … / v1: ctime8 mtime8 track_id4 …
  w32(work, tkhd.off + 12 + (version === 1 ? 16 : 8), newId);
  return work;
}

/** Retag a trex box's track_id (off+12 after size/type/version+flags). */
export function retagTrexTrackId(trexU8, newId) {
  const work = mutableCopy(trexU8);
  w32(work, 12, newId);
  return work;
}

/**
 * Read / patch mvhd.next_track_id (the "next free track id" counter). The
 * merged two-track movie must declare a counter ABOVE both track ids.
 */
export function mvhdNextTrackId(mvhdU8) {
  const version = mvhdU8[8];
  // Body layout (after size+type): version+flags, then
  //   v0: ctime4 mtime4 timescale4 duration4 rate4 volume2 reserved2
  //       reserved8 matrix36 predefined24 next_track_id4  → +96
  //   v1: ctime8 mtime8 timescale4 duration8 rate4 volume2 reserved2
  //       reserved8 matrix36 predefined24 next_track_id4  → +108
  return u32(mvhdU8, 8 + (version === 1 ? 108 : 96));
}

export function patchMvhdNextTrackId(mvhdU8, nextId) {
  const work = mutableCopy(mvhdU8);
  const version = work[8];
  w32(work, 8 + (version === 1 ? 108 : 96), nextId);
  return work;
}

/**
 * Merge the video and audio init segments (ftyp+moov each) into ONE dual-track
 * movie init: a single moov holding the video trak (id 1) and the audio trak
 * (retagged to id 2), one mvex with both trexes, mvhd counter patched.
 */
export function mergeInitSegments(videoInitBuf, audioInitBuf) {
  const vFtyp = firstBox(videoInitBuf, "ftyp");
  const vMoov = firstBox(videoInitBuf, "moov");
  const aMoov = firstBox(audioInitBuf, "moov");
  if (!vFtyp || !vMoov || !aMoov) {
    throw new Error("mergeInitSegments: init segments must contain ftyp+moov (video) and moov (audio)");
  }

  const vChildren = boxes(videoInitBuf, vMoov.off + 8, vMoov.off + vMoov.size);
  const aChildren = boxes(audioInitBuf, aMoov.off + 8, aMoov.off + aMoov.size);

  const view = (buf, b) => buf.subarray(b.off, b.off + b.size);
  const vTrakBox = vChildren.find((b) => b.type === "trak");
  const vMvhdBox = vChildren.find((b) => b.type === "mvhd");
  const vMvexBox = vChildren.find((b) => b.type === "mvex");
  const aTrakBox = aChildren.find((b) => b.type === "trak");
  const aMvexBox = aChildren.find((b) => b.type === "mvex");
  if (!vTrakBox || !vMvhdBox || !aTrakBox) {
    throw new Error("mergeInitSegments: expected mvhd+trak in video moov and trak in audio moov");
  }

  // Audio trak → track_id 2.
  const audioTrak = retagTrakTrackId(view(audioInitBuf, aTrakBox), 2);

  // One mvex holding every trex: video's (id 1) + audio's (retagged to 2).
  // mehd (movie-fragment duration for ONE source) is dropped — durations of
  // two separate sources must not be blended into the merged movie box.
  const trexes = [];
  if (vMvexBox) {
    for (const c of boxes(videoInitBuf, vMvexBox.off + 8, vMvexBox.off + vMvexBox.size)) {
      if (c.type === "trex") trexes.push(view(videoInitBuf, c));
    }
  }
  if (aMvexBox) {
    for (const c of boxes(audioInitBuf, aMvexBox.off + 8, aMvexBox.off + aMvexBox.size)) {
      if (c.type === "trex") trexes.push(retagTrexTrackId(view(audioInitBuf, c), 2));
    }
  }
  const mvex = trexes.length ? buildBox("mvex", trexes) : null;

  // mvhd's next_track_id must exceed every track id in the file.
  const mvhdPatched = patchMvhdNextTrackId(
    view(videoInitBuf, vMvhdBox),
    Math.max(mvhdNextTrackId(view(videoInitBuf, vMvhdBox)), 3),
  );

  // Assemble: mvhd, video trak, audio trak, mvex, then video moov's leftovers.
  const moovChildren = [mvhdPatched, view(videoInitBuf, vTrakBox), audioTrak];
  if (mvex) moovChildren.push(mvex);
  for (const b of vChildren) {
    if (b.type !== "mvhd" && b.type !== "trak" && b.type !== "mvex") moovChildren.push(view(videoInitBuf, b));
  }
  return concatU8([view(videoInitBuf, vFtyp), buildBox("moov", moovChildren)]);
}

// --- fragment handling ------------------------------------------------------

/**
 * Strip segment-level boxes (styp, sidx, prft, anything before the first moof)
 * and return the [moof … mdat] range — the byte span that is valid inside a
 * movie fragment sequence. Throws when the buffer holds no moof (HTML error
 * page, truncated fetch).
 */
export function stripToFragment(segBuf) {
  const list = boxes(segBuf);
  const moof = list.find((b) => b.type === "moof");
  if (!moof) throw new Error("segment has no moof box");
  const mdat = list.find((b) => b.type === "mdat");
  const end = mdat ? mdat.off + mdat.size : segBuf.length;
  return segBuf.subarray(moof.off, end);
}

/**
 * Retag every traf's tfhd track_id inside a stripped fragment. The audio
 * fragments come from an audio-only init where the audio track WAS track 1 —
 * after the init merge retags audio to track 2, every audio fragment must say
 * 2 as well, or the demuxer routes mp4a bytes into the h264 decoder (verified:
 * "Invalid NAL unit size" + 0 audio packets). tfhd.track_id is ALWAYS present
 * (mandatory field at tfhd+12). Returns a mutable copy when patched.
 */
export function retagFragmentTrackId(fragBuf, newId) {
  const work = mutableCopy(fragBuf);
  let patched = 0;
  for (const moof of boxes(work)) {
    if (moof.type !== "moof") continue;
    for (const traf of boxes(work, moof.off + 8, moof.off + moof.size)) {
      if (traf.type !== "traf") continue;
      const tfhd = findBox(work, traf.off + 8, traf.size - 8, "tfhd");
      if (tfhd) {
        w32(work, tfhd.off + 12, newId);
        patched += 1;
      }
    }
  }
  if (!patched) throw new Error("retagFragmentTrackId: fragment has no tfhd");
  return work;
}

// --- playlist parsing -------------------------------------------------------

/**
 * Parse an HLS master playlist into its video variants and audio renditions.
 * Returns { variantUrl, variants, audioRenditions } — absolute URLs.
 * variants: [{ uri, bandwidth, width, height }] (bandwidth/resolution from
 * EXT-X-STREAM-INF; our packages carry one variant with BANDWIDTH only, so
 * width/height stay null there — the route reads real dims from the init's
 * tkhd instead).
 */
export function parseMasterPlaylist(text, baseUrl) {
  const resolve = (u) => new URL(u, baseUrl).toString();
  const lines = text.split(/\r?\n/).map((l) => l.trim());
  const out = { variantUrl: null, variants: [], audioRenditions: [] };
  let pendingVariant = null;
  for (const line of lines) {
    if (!line) continue;
    if (line.startsWith("#EXT-X-MEDIA:")) {
      // Audio renditions are self-contained: TYPE=AUDIO + URI names their own
      // media playlist (ffmpeg writes TYPE=AUDIO unquoted, NAME="Tamil" quoted
      // — parseHlsAttributes handles both).
      const attrs = parseHlsAttributes(line.slice("#EXT-X-MEDIA:".length));
      if ((attrs.get("TYPE") || "").toUpperCase() === "AUDIO" && attrs.get("URI")) {
        out.audioRenditions.push({
          url: resolve(attrs.get("URI")),
          name: attrs.get("NAME") || "Audio",
          language: attrs.get("LANGUAGE") || "",
          default: (attrs.get("DEFAULT") || "").toUpperCase() === "YES",
        });
      }
    } else if (line.startsWith("#EXT-X-STREAM-INF:")) {
      const attrs = parseHlsAttributes(line.slice("#EXT-X-STREAM-INF:".length));
      const resolution = attrs.get("RESOLUTION") || "";
      const rw = /^(\d+)x(\d+)$/.exec(resolution);
      pendingVariant = {
        bandwidth: Number(attrs.get("BANDWIDTH")) || null,
        width: rw ? Number(rw[1]) : null,
        height: rw ? Number(rw[2]) : null,
      };
    } else if (!line.startsWith("#")) {
      // The URI after an EXT-X-STREAM-INF completes that variant.
      const variant = pendingVariant || { bandwidth: null, width: null, height: null };
      pendingVariant = null;
      variant.uri = resolve(line);
      out.variants.push(variant);
      if (!out.variantUrl) out.variantUrl = variant.uri;
    }
  }
  return out;
}

/** Parse `KEY="val",KEY2=val2` attribute lists from an HLS tag body. */
function parseHlsAttributes(body) {
  const map = new Map();
  const re = /([A-Z0-9-]+)=\"([^\"]*)\"|([A-Z0-9-]+)=([^,]*)/g;
  let m;
  while ((m = re.exec(body))) {
    if (m[1]) map.set(m[1], m[2]);
    else if (m[3]) map.set(m[3], m[4]);
  }
  return map;
}

/**
 * Parse an HLS media playlist (video.m3u8 / audio-K.m3u8) into segment URLs.
 * Returns { initUrl, segments: [url…], durationSec } — absolute URLs.
 */
export function parseMediaPlaylist(text, baseUrl) {
  const resolve = (u) => new URL(u, baseUrl).toString();
  const lines = text.split(/\r?\n/).map((l) => l.trim());
  const out = { initUrl: null, segments: [], durationSec: 0 };
  let pendingMap = null;
  for (const line of lines) {
    if (!line) continue;
    if (line.startsWith("#EXT-X-MAP:")) {
      const m = /URI=\"([^\"]+)\"/.exec(line);
      if (m) pendingMap = resolve(m[1]);
    } else if (line.startsWith("#EXTINF:")) {
      const d = parseFloat(line.slice(8));
      if (Number.isFinite(d)) out.durationSec += d;
    } else if (!line.startsWith("#")) {
      out.segments.push(resolve(line));
      if (pendingMap) {
        out.initUrl = pendingMap;
        pendingMap = null;
      }
    }
  }
  return out;
}

/** Sanitize a title into a safe download filename component. */
export function safeFilename(title, ext = "mp4") {
  const base = String(title || "download")
    .replace(/[\\/:*?\"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
  return `${base || "download"}.${ext}`;
}

/**
 * Read the display dimensions from an init segment's tkhd (the track header
 * carries width/height as 16.16 fixed-point in its LAST 8 bytes). Works on a
 * full init (ftyp+moov) or a moov/trak view. Returns { width, height } or
 * nulls when no video tkhd is present (audio-only init).
 */
export function readTkhdDimensions(buf) {
  // Find the first tkhd anywhere below the first moov.
  const CONT = new Set(["moov", "trak", "edts", "mdia", "minf", "stbl", "mvex"]);
  let tkhd = null;
  const scan = (off, end) => {
    for (const b of boxes(buf, off, end)) {
      if (b.type === "tkhd") {
        tkhd = b;
        return;
      }
      if (CONT.has(b.type)) scan(b.off + 8, b.off + b.size);
      if (tkhd) return;
    }
  };
  scan(0, buf.length);
  if (!tkhd) return { width: null, height: null };
  const version = buf[tkhd.off + 8];
  // v0 body: … matrix(36) then width(4) height(4) → +84 from box start;
  // v1 body: 8-byte times push the matrix 16 bytes further → +100.
  const wOff = tkhd.off + (version === 1 ? 100 : 84);
  const hOff = wOff + 4;
  if (hOff + 4 > tkhd.off + tkhd.size) return { width: null, height: null };
  return { width: u32(buf, wOff) >>> 16, height: u32(buf, hOff) >>> 16 };
}
